import { useEffect, useState } from 'react';
import { draftOf } from '@/features/common/DateRangeFilter/dateRange';
import type { DateRangeEnd } from '@/features/common/DateRangeFilter/types/DateRangeEnd';

/**
 * The two fields of a DateRangeFilter as text: what each holds while a date is being typed into it,
 * and whether what was typed could not be read as one.
 */
export function useDateRangeDrafts(from: string | null, to: string | null) {
  // What is in each field while it is being typed, before it is read as a date.
  const [drafts, setDrafts] = useState<Record<DateRangeEnd, string>>({
    from: draftOf(from),
    to: draftOf(to),
  });
  const [invalid, setInvalid] = useState<Record<DateRangeEnd, boolean>>({
    from: false,
    to: false,
  });

  // Each field follows its own end of the filter — a choice in the calendar, a reset, Back. One per
  // end, not one for both: a single effect on [from, to] rewrote both fields whenever either end
  // changed, so picking "De la" in the calendar wiped a date half-typed into "Până la".
  useEffect(() => {
    setDrafts((prev) => ({ ...prev, from: draftOf(from) }));
    setInvalid((prev) => ({ ...prev, from: false }));
  }, [from]);
  useEffect(() => {
    setDrafts((prev) => ({ ...prev, to: draftOf(to) }));
    setInvalid((prev) => ({ ...prev, to: false }));
  }, [to]);

  return {
    drafts,
    invalid,
    setDraft: (end: DateRangeEnd, text: string) => setDrafts((prev) => ({ ...prev, [end]: text })),
    setInvalidEnd: (end: DateRangeEnd, isInvalid: boolean) =>
      setInvalid((prev) => ({ ...prev, [end]: isInvalid })),
  };
}
